import { GiftCard } from '@vend/types'
import { VdToastNotificationService } from '@vend/vend.ui'
import includes from 'lodash/includes'
import ngRedux from 'ng-redux'
import uuid from '@vend/vend-uuid'

import { GIFTCARD_PROVIDER_SETTINGS } from '../../components/gift-cards/provider-settings'
import { giftCardFromBalancesApi } from '../../components/gift-cards/utils'
import * as WR_GIFT_CARDS from '../../constants'
import { containsGiftCardByNumber } from '../../functions/sale'
import { RegisterManager } from '../../managers/register-manager'
import { UserManager } from '../../managers/user-manager'
import { selectLineItems } from '../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../util/feature-cache'
import { dateFormat } from '../../filters/wr-date-format'
import {
  AbstractGiftCardsController,
  GiftCardSettings,
} from './abstract-gift-cards'

export type GiftCardRedeemed = {
  amountTendered: number
  source: string
  source_id: string
}

export interface OmniGiftCardSettings extends GiftCardSettings {
  expiry_period: number
  expiry_period_type: string
  predefined_amounts: number[]
  redeem_enabled: boolean
  sell_enabled: boolean
  channel: string
}

interface NewGiftCard extends GiftCard {
  value: number
}

export class BalancesGiftCardsController extends AbstractGiftCardsController {
  public customAmount: boolean = true

  constructor(
    close: (args: GiftCardRedeemed) => void,
    options: any,
    registerManager: RegisterManager,
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly BalancesResource: any,
    private readonly userManager: UserManager,
    private readonly $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'

    super(
      GIFTCARD_PROVIDER_SETTINGS.BALANCES,
      close,
      options.amount,
      /^[-0-9a-zA-Z]{1,64}$/,
      0, // Default value for min code length, meant to be overridden by getGiftCardCodeMinChars
      64,
      options.workflow,
      registerManager.getActiveOutlet(),
      {
        min: 0.01,
        decimalPlaces: 2,
        allowedPrefix: registerManager.getActiveOutlet().currency_symbol,
        max: undefined,
      }
    )
    this.retrieveRetailerGiftCardSettings()
  }

  public hasOmniGiftCards(): boolean {
    return getFeatureFromCache('omni_gift_cards')
  }

  /**
   * Gives us the search modal title
   */
  public getSearchModalTitle(): string {
    if (this.isSellingWorkflow()) {
      return 'Add new or manage existing gift card.'
    }
    return 'Scan or Enter a Gift Card Number'
  }

  /**
   * Gives us the search modal text
   */
  public getSearchModalText(): string {
    if (this.isSellingWorkflow()) {
      return 'Add a new gift card to the sale, check the balance of an existing gift card, or reload an existing gift card.'
    }
    return 'Check an existing card’s status and balance.'
  }

  /**
   * Retrieve the retailer gift card settings
   */
  public retrieveRetailerGiftCardSettings() {
    if (!this.hasOmniGiftCards()) {
      return
    }
    this.state = WR_GIFT_CARDS.states.LOADING
    return this.BalancesResource.getRetailerGiftCardSettings()
      .then((settings: any) => {
        this.giftCardSettings = settings
        this.valueInputOptions.min = settings.min_value || 0.01
        this.valueInputOptions.max = settings.max_value || undefined
        this.state = this.workflow
      })
      .catch((response: any) => {
        if (response.status === 0) {
          const message = this.isSellingWorkflow()
            ? 'Gift cards can only be sold when the Sell screen is online.'
            : 'Gift cards can not be redeemed when the Sell screen is offline.'

          this.vdToastNotificationService.negative({ message })
          return
        }

        if (response.status >= 500) {
          this.state = WR_GIFT_CARDS.states.SERVER_ERROR
          this.vdToastNotificationService.dismiss()
          return
        }

        this.vdToastNotificationService.negative({
          message: 'Sorry, something went wrong. Please try again.',
        })
        this.state = this.workflow
      })
  }

  /**
   * Returns the display status of the gift card.
   */
  public getGiftCardDisplayStatus(): string {
    const status = (this.giftCard as GiftCard).status
    const hasOmniGiftCards = this.hasOmniGiftCards()
    if (!hasOmniGiftCards) {
      return status
    }
    switch (status) {
      case 'VOIDED':
        return 'CANCELED'
      default:
        return status ?? 'INACTIVE'
    }
  }

  /**
   * Retrieve the details of the current gift card.
   */
  public retrieveGiftCard() {
    this.waiting = true
    const giftCardNumber = this.giftCard.code

    // When selling a gift card, prevent another with the same number from being added to the sale
    if (
      containsGiftCardByNumber(
        selectLineItems(this.$ngRedux.getState()),
        giftCardNumber
      )
    ) {
      this.vdToastNotificationService.negative({
        message:
          'This gift card has been added to the sale. Please use a different gift card number.',
      })

      this.waiting = false
      return
    }

    return this.BalancesResource.getGiftCard(giftCardNumber)
      .then((giftCard: any) => {
        this.giftCard = giftCardFromBalancesApi(giftCard)

        return this.userManager
          .hydrateUsers(giftCard.gift_card_transactions)
          .then((transactions: any) => {
            this.calculateTransactionsBalance(transactions)
            // Change state to view gift card details
            this.state = WR_GIFT_CARDS.states.VIEW_DETAILS
          })
      })
      .catch((response: any) => {
        switch (response.status) {
          case 0: {
            const message = this.isSellingWorkflow()
              ? 'Gift cards can only be sold when the Sell screen is online.'
              : 'Gift cards can not be redeemed when the Sell screen is offline.'

            this.vdToastNotificationService.negative({ message })
            break
          }

          case 404:
            if (this.isSellingWorkflow()) {
              // If expiry options have been set by the retailer, an expected expiry date will be returned as part of
              // the response. This then gets attached to the gift card product when added to the sale.
              // See saleManager.addGiftCardItem.
              ;(this.giftCard as GiftCard).expiresAt = response.headers(
                'expected-expiry-date'
              )
              if (this.hasOmniGiftCards()) {
                const { expiry_period, expiry_period_type } = this
                  .giftCardSettings as OmniGiftCardSettings
                const expiresAt = new Date()

                if (expiry_period && expiry_period_type) {
                  switch (expiry_period_type) {
                    case 'DAYS':
                      expiresAt.setDate(expiresAt.getDate() + expiry_period)
                      break
                    case 'MONTHS':
                      expiresAt.setMonth(expiresAt.getMonth() + expiry_period)
                      break
                    case 'YEARS':
                      expiresAt.setFullYear(
                        expiresAt.getFullYear() + expiry_period
                      )
                      break
                    default:
                      throw new Error(
                        `Unknown expiry type: ${expiry_period_type}`
                      )
                  }

                  const giftCard = this.giftCard as GiftCard
                  giftCard.expiresAt = expiresAt.toISOString()
                  giftCard.status = 'INACTIVE'
                }
              }

              this.state = WR_GIFT_CARDS.states.SET_VALUE
            } else {
              this.vdToastNotificationService.negative({
                message:
                  'Gift card not activated. Please check again or enter another number.',
              })
            }
            break

          default:
            this.vdToastNotificationService.negative({
              message: 'Sorry, something went wrong. Please try again.',
            })
            break
        }
      })
      .finally(() => {
        this.waiting = false
      })
  }

  /**
   * Makes a payment against the provided gift card.
   *
   * @method payWithGiftCard
   */
  public payWithGiftCard() {
    const payableGiftCardAmount = this.getPayableAmount()
    const giftCardNumber = this.giftCard.code

    // Prevent modal from being closed while we wait for the transaction to complete
    this.dismissible = false
    this.waiting = true

    this.BalancesResource.postGiftCardTransaction(giftCardNumber, {
      type: 'REDEEMING',
      amount: -Number(payableGiftCardAmount),
    })
      .then((transactionResponse: { id: string }) => {
        // TODO: add more type information to transactionResponse
        this.close({
          amountTendered: payableGiftCardAmount,
          source: 'Vend Gift Card',
          source_id: transactionResponse.id,
        })
      })
      .catch(() => {
        this.vdToastNotificationService.negative({
          message: 'Sorry, something went wrong. Please try again.',
        })

        this.waiting = false
        this.dismissible = true
      })
  }

  /**
   * Returns true if the gift card can be recharged, false otherwise.
   */
  public isRechargeable(): boolean {
    const hasOmniGiftCards = this.hasOmniGiftCards()

    if (!hasOmniGiftCards) {
      return true
    }

    return !includes(['VOIDED', 'EXPIRED'], (this.giftCard as GiftCard).status)
  }

  public getValueForm(): any {
    const formName = this.hasOmniGiftCards()
      ? 'COMPONENT_VALUE_FORM'
      : `COMPONENT_${this.state}_FORM_LEGACY`
    return this.getTemplateUrl(formName)
  }

  public addGiftCardReloadToSale() {
    const value = this.hasOmniGiftCards()
      ? (this.giftCard as NewGiftCard).value
      : this.reloadBalance

    this.close({
      code: (this.giftCard as GiftCard).code,
      value,
      isReload: true,
      clientId: uuid(),
      expiresAt: (this.giftCard as GiftCard).expiresAt,
    })
  }

  public hasPredefinedAmounts(): boolean {
    return (
      (this.giftCardSettings as OmniGiftCardSettings)?.predefined_amounts
        ?.length > 0
    )
  }

  public getColumnClass(first: boolean = false): string {
    // if not the first column and three columns are present
    if (
      this.state === WR_GIFT_CARDS.states.ADD_BALANCE &&
      this.getGiftCardExpiryDate()
    ) {
      if (first) {
        return 'wr-gift-card-mw40'
      }
      return 'wr-gift-card-mw30'
    }

    // if it is the first column and another column is present
    if (
      this.state === WR_GIFT_CARDS.states.ADD_BALANCE ||
      this.getGiftCardExpiryDate()
    ) {
      return 'wr-gift-card-mw50'
    }

    // if it is the first and only column
    return 'wr-gift-card-mw100'
  }

  public getGiftCardExpiryDate(): string {
    if ((this.giftCard as GiftCard).expiresAt) {
      return dateFormat(
        (this.giftCard as GiftCard).expiresAt,
        'MMM DD, YYYY',
        this.outlet.time_zone
      )
    }
    return this.hasOmniGiftCards() ? '' : 'None'
  }

  public getGiftCardCodeMinChars(): number {
    if (
      this.hasOmniGiftCards() &&
      getFeatureFromCache('omni_gift_cards_allow_short_codes') &&
      this.isSellingWorkflow()
    ) {
      return 1
    }
    if (this.hasOmniGiftCards() && this.isSellingWorkflow()) {
      return 8
    }
    return this.giftCardCodeMinChars
  }
}
